import { Button, Card, Dialog, Input } from "@app/ui";
import { useEffect, useState } from "react";
import { useI18n } from "../../lib/useI18n.js";
import { useAuthStore } from "../../stores/index.js";
import { toast } from "../../stores/toast.store.js";

/** Account sign-up / sign-in for multi-device sync (F-PROD). Tokens live on this
 *  device only and are never written to a backup. */
export function AccountCard() {
  const { t } = useI18n();
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const busy = useAuthStore((s) => s.busy);
  const error = useAuthStore((s) => s.error);
  const loaded = useAuthStore((s) => s.loaded);
  const load = useAuthStore((s) => s.load);
  const signup = useAuthStore((s) => s.signup);
  const login = useAuthStore((s) => s.login);
  const loginWithGoogle = useAuthStore((s) => s.loginWithGoogle);
  const googleAvailable = useAuthStore((s) => s.googleAvailable);
  const logout = useAuthStore((s) => s.logout);
  const deleteAccount = useAuthStore((s) => s.deleteAccount);
  const requestPasswordReset = useAuthStore((s) => s.requestPasswordReset);
  const resendVerification = useAuthStore((s) => s.resendVerification);
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!loaded) void load();
  }, [loaded, load]);

  const signedIn = Boolean(accessToken);

  async function submit() {
    const ok = mode === "signup" ? await signup(email, password) : await login(email, password);
    if (ok) setPassword("");
  }

  return (
    <Card className="p-4">
      <h2 className="mb-2 font-medium">{t("sync.account")}</h2>

      {signedIn ? (
        <div className="flex flex-col gap-3">
          {user?.email_verified === false ? (
            <div className="flex items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-700/60 dark:bg-amber-950/40">
              <p className="text-xs text-amber-800 dark:text-amber-200">
                {t("sync.emailUnverified")}
              </p>
              <button
                type="button"
                className="shrink-0 text-xs font-medium text-amber-900 underline-offset-2 hover:underline dark:text-amber-100"
                onClick={() => void resendVerification()}
              >
                {t("sync.resend")}
              </button>
            </div>
          ) : null}
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              {t("sync.signedInAs", { email: "" })}<span className="font-medium text-foreground">{user?.email}</span>
            </p>
            <Button variant="outline" disabled={busy} onClick={() => void logout()}>
              {t("sync.logOut")}
            </Button>
          </div>
          <div className="flex items-center justify-between gap-2 border-t pt-3">
            <p className="text-xs text-muted-foreground">
              {t("sync.deleteAccountDescription")}
            </p>
            <Button variant="destructive" disabled={busy} onClick={() => setConfirmDelete(true)}>
              {t("sync.deleteAccount")}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <p className="mb-3 text-sm text-muted-foreground">
            {mode === "signup"
              ? t("sync.signupDescription")
              : t("sync.loginDescription")}
          </p>
          {googleAvailable() ? (
            <Button
              variant="outline"
              className="mb-3 w-full"
              disabled={busy}
              onClick={() => void loginWithGoogle()}
            >
              {busy ? "..." : t("sync.continueWithGoogle")}
            </Button>
          ) : null}
          <form
            className="flex flex-col gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <Input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
            <Input
              type="password"
              autoComplete={mode === "signup" ? "new-password" : "current-password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === "signup" ? t("sync.passwordSignup") : t("sync.password")}
            />
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={busy || !email || !password}>
                {busy ? "..." : mode === "signup" ? t("sync.createAccount") : t("sync.signIn")}
              </Button>
              <button
                type="button"
                className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                onClick={() => setMode(mode === "signup" ? "login" : "signup")}
              >
                {mode === "signup" ? t("sync.haveAccount") : t("sync.newHere")}
              </button>
              {mode === "login" ? (
                <button
                  type="button"
                  className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                  onClick={() => {
                    if (!email.trim()) {
                      toast.info(t("sync.enterEmailFirst"));
                      return;
                    }
                    void requestPasswordReset(email);
                  }}
                >
                  {t("sync.forgotPassword")}
                </button>
              ) : null}
              {error ? <span className="text-xs text-destructive">{error}</span> : null}
            </div>
          </form>
        </>
      )}

      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={t("sync.deleteAccount")}
        className="max-w-md"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-foreground">
            {t("sync.deleteAccountBody", { email: user?.email ? ` (${user.email})` : "" })}
          </p>
          <div className="flex flex-col gap-2">
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() => {
                setConfirmDelete(false);
                void deleteAccount();
              }}
            >
              {t("sync.deleteMyAccount")}
            </Button>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      </Dialog>
    </Card>
  );
}
